import axios from "axios";
import posthog from 'posthog-js'
import { env } from "../../config/env";
import { authStorage } from "../storage/authStorage";
import { emitQuotaExceeded, extractQuotaError } from "./quotaEvents";
import { adminStorage } from "../storage/adminStorage";
import { orgAdminStorage } from "../storage/orgAdminStorage";

export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
});

const IST_TIME_ZONE = "Asia/Kolkata";
const ISO_DATETIME_LIKE =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/;

function maybeToISTIso(value: unknown): unknown {
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!ISO_DATETIME_LIKE.test(text)) return value;
  // Real bug this fixes: a datetime string with NO timezone marker (the
  // regex above explicitly allows one to be absent) is what `new Date()`
  // treats as the BROWSER'S OWN LOCAL time per the JS spec — on an
  // IST-configured browser, an offset-less UTC value like
  // "2026-09-03T17:37:00" (meaning 23:07 IST) was silently misread as
  // "17:37 IST" instead, then re-stamped with an explicit "+05:30" suffix
  // below — baking a 5:30-hour error into a now-confidently-labeled string.
  // Every backend value reaching here is UTC when it has no offset (this
  // app never emits naive-and-locally-intended timestamps), so an
  // offset-less string must be parsed as UTC — exactly the same rule
  // `dateUtils.ts`'s `parseInstant` already follows correctly.
  const hasOffset = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(text);
  const normalized = text.includes(" ") ? text.replace(" ", "T") : text;
  const parsed = new Date(hasOffset ? normalized : `${normalized}Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(parsed);
  const lookup = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  return `${lookup("year")}-${lookup("month")}-${lookup("day")}T${lookup("hour")}:${lookup("minute")}:${lookup("second")}+05:30`;
}

function deepNormalizeDatesToIST<T>(input: T): T {
  if (Array.isArray(input)) {
    return input.map((v) => deepNormalizeDatesToIST(v)) as T;
  }
  if (input && typeof input === "object") {
    const entries = Object.entries(input as Record<string, unknown>).map(([k, v]) => [
      k,
      deepNormalizeDatesToIST(v),
    ]);
    return Object.fromEntries(entries) as T;
  }
  return maybeToISTIso(input) as T;
}

apiClient.interceptors.request.use((config) => {
  // Only inject the regular user token if the caller hasn't already set
  // an Authorization header (e.g. admin routes pass their own admin JWT).
  if (!config.headers.Authorization) {
    const token = authStorage.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  try {
    const user = authStorage.getUser();
    posthog.capture('api_request', { url: config.url, method: config.method, user_id: user?.id });
  } catch (e) {}
  return config;
});

apiClient.interceptors.response.use((response) => {
  if (response.config.responseType !== "blob" && response.config.responseType !== "arraybuffer") {
    response.data = deepNormalizeDatesToIST(response.data);
  }
  try {
    const user = authStorage.getUser();
    posthog.capture('api_response', { url: response.config.url, method: response.config.method, status: response.status, user_id: user?.id });
  } catch (e) {}
  return response;
});

apiClient.interceptors.response.use(undefined, (error) => {
  try {
    const config = error.config || {};
    const user = authStorage.getUser();
    posthog.capture('api_error', { url: config.url, method: config.method, message: error.message, user_id: user?.id });
  } catch (e) {}
  try {
    const quota = extractQuotaError(error);
    if (quota) emitQuotaExceeded(quota);
  } catch (e) {}
  try {
    // A restricted account gets its token blocklisted server-side on this
    // very call (see get_current_user in auth/routes.py) — mirror that here
    // by dropping the local session immediately. Land on the home page
    // rather than /login so the (now signed-out) user can keep browsing as
    // a guest instead of being funneled into a "log back in" prompt.
    const code = error?.response?.data?.detail?.code;
    if (code === "ACCOUNT_RESTRICTED") {
      authStorage.clearSession();
      if (typeof window !== "undefined" && window.location.pathname !== "/") {
        window.location.href = "/";
      }
    }
  } catch (e) {}
  try {
    // Admin requests carry their own bearer token (see adminHeaders() in
    // features/admin/service.ts) rather than the regular authStorage token.
    // A 401, or an ACCOUNT_RESTRICTED-style 403, on one of those means the
    // admin's token_version was bumped server-side (password reset/forced
    // logout) — clear the stale admin session and hard-reload to the admin
    // login page so a superadmin session invalidated elsewhere can't keep
    // acting on a dead token.
    const status = error?.response?.status;
    const code = error?.response?.data?.detail?.code;
    const authHeader: string | undefined = error?.config?.headers?.Authorization;
    const adminToken = adminStorage.getToken();
    const wasAdminRequest = !!adminToken && authHeader === `Bearer ${adminToken}`;
    if (wasAdminRequest && (status === 401 || status === 403 || code === "ACCOUNT_RESTRICTED")) {
      adminStorage.clearSession();
      if (typeof window !== "undefined" && window.location.pathname !== "/admin/login") {
        window.location.href = "/admin/login";
      }
    }
  } catch (e) {}
  try {
    // Same treatment for the org-admin console, which carries its own separate
    // bearer token (see orgAdminHeaders() in features/org-admin/service.ts).
    // require_org_admin re-checks the user doc and the org record on EVERY
    // request, so a demotion, removal or org suspension turns into a 403 here
    // mid-session — drop the org-admin session rather than leave the console
    // looping on a token the server no longer honours. Only the org-admin keys
    // are cleared, so a superadmin or buyer/seller session in the same browser
    // is untouched.
    const status = error?.response?.status;
    const authHeader: string | undefined = error?.config?.headers?.Authorization;
    const orgAdminToken = orgAdminStorage.getToken();
    const wasOrgAdminRequest = !!orgAdminToken && authHeader === `Bearer ${orgAdminToken}`;
    if (wasOrgAdminRequest && (status === 401 || status === 403)) {
      orgAdminStorage.clearSession();
      if (typeof window !== "undefined" && window.location.pathname !== "/org-admin/login") {
        window.location.href = "/org-admin/login";
      }
    }
  } catch (e) {}
  return Promise.reject(error);
});
